import { getWindow } from "./env-util";

/**
 * Queues a function to be called during a browser's idle periods
 * Use requestIdleCallback if available or fall back to setTimeout
 * @param {Function} callback A reference to a function that should be called
 */
function onIdle(callback) {
    const _window = getWindow();

    if ('requestIdleCallback' in _window) {
        _window.requestIdleCallback(callback);
    } else {
        setTimeout(callback, 0);
    }
}

/**
 * Execute callback if ready state is complete or else register callback on load event
 * @param {Function} callback A reference to a function that should be called
 */
function onReadyStateCompleteOrLoad(callback) {
    const _window = getWindow();

    if (_window && _window.addEventListener) {
        // using readyState in case sdk is loaded asynchronously and load event has already fired
        /* istanbul ignore next */
        if (window.document.readyState === 'complete') {
            callback();
        } else {
            window.addEventListener('load', callback);
        }
    }
}

/**
 *  Get the document location href if available. Otherwise return undefined so
 *  the value will be removed when JSON.stringify is applied to the payload.
 *  @returns {string|undefined} - Returns a string when available
 */
function getPageUrl() {
    const _window = getWindow();
    const _location = _window.location;
    return _location && _location.href || undefined;
}

/**
 *  Get the document referrer if available. Otherwise return undefined so the
 *  value will be removed when JSON.stringify is applied to the payload.
 *  @returns {string|undefined} - Returns a string when available
 */
function getReferrer() {
    const _window = getWindow();
    const _document = _window.document;
    return _document && _document.referrer || undefined;
}

/**
 * @returns {number} - Returns the width of the window's layout viewport in pixels
 */
 function getViewportWidth() {
    const _window = getWindow();
    return Math.round(_window.innerWidth) || 0;
}

/**
 * @returns {number} - Returns the height of the window's layout viewport in pixels
 */
 function getViewportHeight() {
    const _window = getWindow();
    return Math.round(_window.innerHeight) || 0;
}

/**
 * @returns {number} - Returns screen resolution width in pixels
 */
function getResolutionWidth() {
    return Math.round(getScreenWidth() * getDevicePixelRatio());
}

/**
 * @returns {number} - Returns screen resolution height in pixels
 */
function getResolutionHeight() {
    return Math.round(getScreenHeight() * getDevicePixelRatio());
}

/**
 * @returns {number} - Returns screen width in pixels
 */
function getScreenWidth() {
    const _screen = getWindow().screen;
    return _screen && _screen.width || 0;
}

/**
 * @returns {number} - Returns screen height in pixels
 */
function getScreenHeight() {
    const _screen = getWindow().screen;
    return _screen && _screen.height || 0;
}

/**
 * @returns {number} - Returns the ratio of the resolution in physical pixels to the resolution in CSS pixels for the current display device
 */
function getDevicePixelRatio() {
    const _window = getWindow();
    return _window.devicePixelRatio || 0;
}

export {
    onIdle,
    getPageUrl,
    getReferrer,
    getViewportWidth,
    getViewportHeight,
    getResolutionWidth,
    getResolutionHeight,
    onReadyStateCompleteOrLoad,
};
